import React, { useEffect, useState } from 'react';
import type { FC } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { Typography } from '@mui/material';
import { DebouncedState, useDebouncedCallback } from 'use-debounce';

import {
  EmployeesToAddForInterviewModuleQuery,
  useEmployeesToAddForInterviewModuleLazyQuery,
} from 'src/generated/mloop-graphql';

import { BaseEmployeePicker } from 'src/entities/EmployeePicker';
import { EmployeeFragment } from 'src/entities/EmployeePicker/EmployeeGraphQL';

import { ArrayElement, ElementType } from 'src/types/utils';

import { DEBOUNCE_TIMEOUT, PAGE_SIZE } from 'src/constants';

export type EmployeeResult = ArrayElement<ElementType<EmployeesToAddForInterviewModuleQuery['employees']>['items']>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GQLEmployeesToAddForInterviewModule = gql`
  ${EmployeeFragment}
  query EmployeesToAddForInterviewModule($input: EmployeesInput!, $interviewModuleId: uuid!) {
    employees(input: $input) {
      nextCursor
      items {
        ...Employee
        interviewModuleMember(interviewModuleId: $interviewModuleId) {
          employeeId
          status
          interviewModuleId
        }
      }
    }
  }
`;

interface Props {
  interviewModuleId: string;
  selectedEmployees: EmployeeResult[];
  placeholder: string;
  onChange: (employeeResult: EmployeeResult[]) => void;
}

const useSearch = (
  val: string,
  hasNext?: boolean
): [searchStr: string, debouncedHandleSearch: DebouncedState<(event: React.ChangeEvent, value: string) => void>] => {
  const [searchStr, setSearchStr] = useState<string>('');
  const handleSearch = (_event: React.ChangeEvent, value: string) => {
    if (hasNext === false && !searchStr) {
      return;
    }
    setSearchStr(value.toLowerCase());
  };
  const debouncedHandleSearch = useDebouncedCallback(handleSearch, DEBOUNCE_TIMEOUT);
  return [searchStr, debouncedHandleSearch];
};

const InterviewModuleEmployeePicker: FC<Props> = ({ interviewModuleId, selectedEmployees, onChange, placeholder }) => {
  const { t } = useTranslation();
  // Note: we dont do pagination here , so having a fetch policy is ok, but when we decide to please remove fetchPolicy or pagination wont work
  const [fetchEmployee, { loading, data, error }] = useEmployeesToAddForInterviewModuleLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  const [searchStr, setSearchStr] = useSearch('', !!data?.employees?.nextCursor);

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    fetchEmployee({
      variables: {
        input: {
          pageInput: {
            limit: PAGE_SIZE,
          },
          isArchived: false,
          isAtsDisabled: false,
          isDirectoryDisabled: false,
          hasAtsId: true,
          search: searchStr,
        },
        interviewModuleId,
      },
    });
  }, [fetchEmployee, interviewModuleId, searchStr]);

  if (error) {
    return <Typography>{t('hiring-dashboard-interviewers:interviewers.employeePicker.error')}</Typography>;
  }

  return (
    <BaseEmployeePicker
      autoFocus
      onInputChange={setSearchStr}
      employees={data?.employees?.items || []}
      loading={loading}
      selectedEmployees={selectedEmployees}
      onChange={onChange}
      placeholder={placeholder}
      filterOptions={(members) => {
        return members.filter((member: EmployeeResult) => {
          return !member?.interviewModuleMember?.employeeId;
        });
      }}
    />
  );
};

export default InterviewModuleEmployeePicker;
